/**
 * Musical scale tables + a snap helper used by the Jukebox editor.
 *
 * A scale is just an ordered list of pitch-classes (semitones from the
 * tonic, 0..11). `nearestInScale` projects an arbitrary MIDI note onto
 * the nearest in-scale note (octave-aware), so scroll/drag can be
 * constrained to a tonality without losing the player's intent.
 *
 * The `none` scale is a no-op (returns the input unchanged) — exposed
 * so the dropdown can sit at "Chromatic — no filter" by default.
 */

export interface ScaleDef {
  /** Stable key used in <option value=…> + persistence. */
  key: string;
  /** Human-readable label rendered in the dropdown. */
  label: string;
  /** Pitch-classes (0..11) relative to the tonic, ascending. Empty = no filter. */
  intervals: ReadonlyArray<number>;
}

const ROOT_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
export type RootName = typeof ROOT_NAMES[number];

export const ROOTS: ReadonlyArray<RootName> = ROOT_NAMES;

/** MIDI pitch-class for a root name (C=0..B=11). */
export function rootPc(name: RootName): number {
  return ROOT_NAMES.indexOf(name);
}

/**
 * Curated catalogue: starts with common tonal scales (major / natural
 * minor / pentatonics / blues), then modes, then a handful of less
 * common but musically interesting choices (harmonic + melodic minor,
 * Gypsy, whole-tone, octatonic, etc.).
 *
 * Order matters — it's the order the dropdown renders.
 */
export const SCALES: ReadonlyArray<ScaleDef> = [
  { key: 'none',           label: 'Chromatic — no filter',     intervals: [] },

  // ── Common tonal ──
  { key: 'major',          label: 'Major (Ionian)',             intervals: [0, 2, 4, 5, 7, 9, 11] },
  { key: 'minor',          label: 'Natural minor (Aeolian)',    intervals: [0, 2, 3, 5, 7, 8, 10] },
  { key: 'pentaMajor',     label: 'Major pentatonic',           intervals: [0, 2, 4, 7, 9] },
  { key: 'pentaMinor',     label: 'Minor pentatonic',           intervals: [0, 3, 5, 7, 10] },
  { key: 'blues',          label: 'Blues (hexatonic)',          intervals: [0, 3, 5, 6, 7, 10] },

  // ── Church modes ──
  { key: 'dorian',         label: 'Dorian',                     intervals: [0, 2, 3, 5, 7, 9, 10] },
  { key: 'phrygian',       label: 'Phrygian',                   intervals: [0, 1, 3, 5, 7, 8, 10] },
  { key: 'lydian',         label: 'Lydian',                     intervals: [0, 2, 4, 6, 7, 9, 11] },
  { key: 'mixolydian',     label: 'Mixolydian',                 intervals: [0, 2, 4, 5, 7, 9, 10] },
  { key: 'locrian',        label: 'Locrian',                    intervals: [0, 1, 3, 5, 6, 8, 10] },

  // ── Minor variants ──
  { key: 'harmonicMinor',  label: 'Harmonic minor',             intervals: [0, 2, 3, 5, 7, 8, 11] },
  { key: 'melodicMinor',   label: 'Melodic minor (ascending)',  intervals: [0, 2, 3, 5, 7, 9, 11] },
  { key: 'phrygianDom',    label: 'Phrygian dominant',          intervals: [0, 1, 4, 5, 7, 8, 10] },

  // ── World / less common ──
  { key: 'hirajoshi',      label: 'Hirajoshi (Japanese)',       intervals: [0, 2, 3, 7, 8] },
  { key: 'inSen',          label: 'In Sen (Japanese)',          intervals: [0, 1, 5, 7, 10] },
  { key: 'gypsy',          label: 'Hungarian gypsy',            intervals: [0, 2, 3, 6, 7, 8, 11] },
  { key: 'doubleHarmonic', label: 'Double harmonic (Byzantine)', intervals: [0, 1, 4, 5, 7, 8, 11] },
  { key: 'enigmatic',      label: 'Enigmatic',                  intervals: [0, 1, 4, 6, 8, 10, 11] },
  { key: 'neapolitanMin',  label: 'Neapolitan minor',           intervals: [0, 1, 3, 5, 7, 8, 11] },

  // ── Symmetric / synthetic ──
  { key: 'wholeTone',      label: 'Whole-tone',                 intervals: [0, 2, 4, 6, 8, 10] },
  { key: 'dimHW',          label: 'Octatonic (half–whole)',     intervals: [0, 1, 3, 4, 6, 7, 9, 10] },
  { key: 'dimWH',          label: 'Octatonic (whole–half)',     intervals: [0, 2, 3, 5, 6, 8, 9, 11] },
  { key: 'augmented',      label: 'Augmented',                  intervals: [0, 3, 4, 7, 8, 11] },
];

const SCALE_BY_KEY = new Map(SCALES.map((s) => [s.key, s]));

export function getScale(key: string): ScaleDef {
  return SCALE_BY_KEY.get(key) ?? SCALES[0]!;
}

/**
 * Project `midi` onto the nearest in-scale note for the given root.
 * Empty interval list → returns `midi` unchanged. Ties go to the
 * upper neighbour so upward scrolls still feel forward-moving.
 */
export function nearestInScale(midi: number, root: RootName, scale: ScaleDef): number {
  const ints = scale.intervals;
  if (ints.length === 0) return midi;
  const tonic = rootPc(root);
  // Search a small window around the candidate so we always find a
  // valid pitch within ±6 semitones.
  let best = midi;
  let bestDist = Infinity;
  for (let d = -6; d <= 6; d++) {
    const cand = midi + d;
    const pc = ((cand - tonic) % 12 + 12) % 12;
    if (!ints.includes(pc)) continue;
    const dist = Math.abs(d);
    // On ties (e.g. d=-1 vs d=+1) prefer the higher candidate.
    if (dist < bestDist || (dist === bestDist && cand > best)) {
      best = cand;
      bestDist = dist;
    }
  }
  return best;
}
